import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    X,
    Star,
    Loader2,
    Building2,
    Calendar,
    Sparkles
} from 'lucide-react';
import { saveContract, unsaveContract, updateSavedContract, SavedContract } from '../services/favorites';
import { formatDate } from '../services/samApi';
import { useAuth } from '../contexts/AuthContext';
import { analyzeOpportunity, getStoredAnalysis, saveAnalysis } from '../services/analysis';
import { Contractor } from '../types';

interface ContractModalProps {
    contract: any;
    contractor: Contractor | null;
    contractorId: string;
    isSaved: boolean;
    onClose: () => void;
    onSaveToggle: (saved: boolean) => void;
    onUpdate?: (updatedContract: any) => void;
}

export default function ContractModal({
    contract: initialContract,
    contractor,
    contractorId,
    isSaved,
    onClose,
    onSaveToggle,
    onUpdate
}: ContractModalProps) {
    const { user } = useAuth();
    const navigate = useNavigate();

    // Local contract state to handle updates
    const [contract, setContract] = useState(initialContract);
    const [status, setStatus] = useState<string>(initialContract.status || 'new');
    const [isAnalyzing, setIsAnalyzing] = useState(false);

    // Unused in simplified modal, but kept for compatibility or removal



    // Update local state if prop changes
    useEffect(() => {
        setContract(initialContract);
        if (initialContract.status) setStatus(initialContract.status);

    }, [initialContract]);

    // Auto-trigger analysis on mount if not analyzed
    useEffect(() => {
        if (!contract.isAnalyzed && !isAnalyzing && !contract.fitScore) {
            handleAnalyze();
        }
    }, [contract.noticeId]);

    const handleAnalyze = async () => {
        if (isAnalyzing) return;
        // Check if already has data (passed via props)
        if (contract.isAnalyzed && contract.fitScore) return;

        setIsAnalyzing(true);
        try {
            // 1. Check DB Cache
            const cached = await getStoredAnalysis(contract.noticeId);
            if (cached) {
                console.log("Using cached analysis for", contract.noticeId);
                const updated = { ...contract, ...cached, isAnalyzed: true };
                setContract(updated);
                if (onUpdate) onUpdate(updated);
            } else {
                // 2. Run AI
                console.log("Running fresh AI analysis for", contract.noticeId);
                // Pass the current contractor profile for deep context
                const safeContractor = contractor || {
                    displayName: 'Guest User',
                    keywords: ['Government Contracting'],
                    naicsCodes: ['541511']
                };
                const analysis = await analyzeOpportunity(contract, safeContractor);

                // 3. Save to DB
                await saveAnalysis(contract.noticeId, analysis);

                const updated = { ...contract, ...analysis, isAnalyzed: true };
                setContract(updated);
                if (onUpdate) onUpdate(updated);
            }
        } catch (error) {
            console.error("Analysis failed:", error);
        } finally {
            setIsAnalyzing(false);
        }
    };

    const handleStatusChange = async (newStatus: string) => {
        if (!isSaved) return;
        setStatus(newStatus);
        try {
            await updateSavedContract(contractorId, contract.noticeId, { status: newStatus as any });
            if (onUpdate) onUpdate({ ...contract, status: newStatus });
        } catch (err) {
            console.error('Failed to update status', err);
            // Revert state if needed, or rely on UI reload
        }
    };

    const agencyName = contract.agency || contract.fullParentPathName?.split('.')[0] || 'Unknown Agency';
    const link = contract.uiLink || `https://sam.gov/opp/${contract.noticeId}/view`;



    const handleSaveToggle = async () => {
        if (!user) {
            alert("Please sign in to save contracts.");
            return;
        }
        if (!contractorId) {
            console.error("Missing contractorId for save.");
            return;
        }

        // Optimistic UI update
        const previousState = isSaved;
        onSaveToggle(!previousState);

        try {
            if (previousState) {
                await unsaveContract(contractorId, contract.noticeId);
            } else {
                const savedContract: SavedContract = {
                    id: contract.noticeId,
                    contractorId: contractorId,
                    savedBy: user.uid,
                    savedAt: new Date().toISOString(),
                    title: contract.title,
                    agency: agencyName,
                    solicitationNumber: contract.solicitationNumber,
                    postedDate: contract.postedDate,
                    responseDeadline: contract.responseDeadline,
                    type: contract.type,
                    naicsCode: contract.naicsCode,
                    uiLink: link,
                    status: 'new', // Default status for pipeline
                    priority: 'medium'
                };
                await saveContract(savedContract);
            }
        } catch (err: any) {
            console.error('Error toggling save:', err);
            // Revert on error
            onSaveToggle(previousState);
            alert(`Failed to save: ${err.message || "Insufficient permissions"}`);
        }
    };


    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/90 backdrop-blur-md" onClick={onClose} />
            <div className="relative z-[110] bg-gray-900 w-full h-full md:h-auto md:max-h-[90vh] md:max-w-4xl md:rounded-3xl shadow-2xl overflow-hidden flex flex-col border border-white/10 ring-1 ring-white/10 text-white">

                {/* AI HERO SECTION */}
                <div className="relative overflow-hidden bg-gradient-to-b from-blue-900/20 to-transparent p-6 md:p-8 flex-shrink-0">
                    <div className="absolute top-0 right-0 p-4 flex gap-2 z-20">
                        {/* Status Select */}
                        {isSaved && (
                            <div className="relative group mr-2">
                                <select
                                    value={status}
                                    onChange={(e) => handleStatusChange(e.target.value)}
                                    className="appearance-none bg-black/40 backdrop-blur-md text-xs font-bold uppercase tracking-widest text-white border border-white/10 rounded-full py-2 pl-4 pr-8 hover:bg-white/10 transition-all cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500"
                                >
                                    <option value="new" className="bg-gray-900">New Lead</option>
                                    <option value="review" className="bg-gray-900">Review</option>
                                    <option value="bid" className="bg-gray-900">Bidding</option>
                                    <option value="submitted" className="bg-gray-900">Submitted</option>
                                    <option value="won" className="bg-gray-900">Won</option>
                                    <option value="lost" className="bg-gray-900">Lost</option>
                                </select>
                                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                                    <div className={`w-2 h-2 rounded-full ${status === 'new' ? 'bg-blue-500' :
                                        status === 'review' ? 'bg-yellow-500' :
                                            status === 'bid' ? 'bg-purple-500' :
                                                status === 'submitted' ? 'bg-indigo-500' :
                                                    status === 'won' ? 'bg-green-500' : 'bg-red-500'
                                        }`} />
                                </div>
                            </div>
                        )}
                        <button
                            onClick={handleSaveToggle}
                            disabled={!contractorId}
                            className={`p-2 rounded-full backdrop-blur-md border border-white/10 transition-all ${isSaved
                                ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                                : 'bg-black/40 text-gray-400 hover:text-white'
                                }`}
                        >
                            <Star className={`w-5 h-5 ${isSaved ? 'fill-current' : ''}`} />
                        </button>
                        <button
                            onClick={() => {
                                onClose();
                                navigate(`/contract/${contract.noticeId}`, { state: { contract } });
                            }}
                            className="hidden md:flex items-center gap-2 px-4 py-2 bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 border border-blue-500/30 rounded-full text-xs font-bold uppercase tracking-wider transition-all mr-2"
                        >
                            <Building2 className="w-4 h-4" />
                            Command Center
                        </button>
                        <button onClick={onClose} className="p-2 rounded-full bg-black/40 hover:bg-white/10 backdrop-blur-md text-gray-400 hover:text-white border border-white/10 transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="flex flex-col md:flex-row gap-8 items-start relative z-10">
                        {/* Scores */}
                        <div className="flex gap-4 w-full md:w-auto justify-center md:justify-start">
                            <div className="flex flex-col items-center">
                                <div className="relative w-24 h-24 md:w-32 md:h-32 flex items-center justify-center">
                                    <svg className="absolute inset-0 w-full h-full transform -rotate-90">
                                        <circle cx="50%" cy="50%" r="45%" stroke="currentColor" strokeWidth="6" className="text-gray-800/50" fill="none" />
                                        <circle cx="50%" cy="50%" r="45%" stroke="currentColor" strokeWidth="6" className={`text-green-500 transition-all duration-1000 ease-out ${isAnalyzing ? 'opacity-30' : ''}`}
                                            strokeDasharray="283" strokeDashoffset={283 - (283 * (contract.fitScore || 0)) / 100} strokeLinecap="round" fill="none"
                                        />
                                    </svg>
                                    <div className="text-center">
                                        {isAnalyzing ? (
                                            <Loader2 className="w-8 h-8 text-green-500 animate-spin opacity-50" />
                                        ) : (
                                            <>
                                                <span className="text-2xl md:text-4xl font-bold text-white block">{contract.fitScore || 0}</span>
                                                <span className="text-[10px] uppercase tracking-widest text-green-400 font-semibold">Fit</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-col items-center">
                                <div className="relative w-24 h-24 md:w-32 md:h-32 flex items-center justify-center">
                                    <svg className="absolute inset-0 w-full h-full transform -rotate-90">
                                        <circle cx="50%" cy="50%" r="45%" stroke="currentColor" strokeWidth="6" className="text-gray-800/50" fill="none" />
                                        <circle cx="50%" cy="50%" r="45%" stroke="currentColor" strokeWidth="6" className={`text-blue-500 transition-all duration-1000 ease-out ${isAnalyzing ? 'opacity-30' : ''}`}
                                            strokeDasharray="283" strokeDashoffset={283 - (283 * (contract.opportunityScore || 0)) / 100} strokeLinecap="round" fill="none"
                                        />
                                    </svg>
                                    <div className="text-center">
                                        {isAnalyzing ? (
                                            <Loader2 className="w-8 h-8 text-blue-500 animate-spin opacity-50" />
                                        ) : (
                                            <>
                                                <span className="text-2xl md:text-4xl font-bold text-white block">{contract.opportunityScore || 0}</span>
                                                <span className="text-[10px] uppercase tracking-widest text-blue-400 font-semibold">Opp</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Summary & Header */}
                        <div className="flex-1 space-y-4">
                            <div>
                                <h1 className="text-xl md:text-2xl font-bold text-white leading-tight mb-2 tracking-tight">
                                    {contract.title}
                                </h1>
                                <div className="flex flex-wrap gap-2 text-sm text-gray-400">
                                    <div className="flex items-center gap-1.5 px-2 py-1 bg-white/5 rounded-md border border-white/5">
                                        <Building2 className="w-3.5 h-3.5" />
                                        {agencyName}
                                    </div>
                                    <div className="flex items-center gap-1.5 px-2 py-1 bg-white/5 rounded-md border border-white/5">
                                        <Calendar className="w-3.5 h-3.5" />
                                        Posted {formatDate(contract.postedDate)}
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-5 relative overflow-hidden group">
                                <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-blue-500 to-purple-500" />
                                <div className="flex items-start gap-3">
                                    <Sparkles className="w-5 h-5 text-blue-400 mt-1 flex-shrink-0 animate-pulse" />
                                    <div>
                                        <h3 className="text-xs font-bold text-blue-400 uppercase tracking-widest mb-1.5">AI Executive Summary</h3>
                                        {isAnalyzing ? (
                                            <div className="space-y-2 max-w-lg">
                                                <div className="h-2 bg-white/10 rounded-full w-3/4 animate-pulse" />
                                                <div className="h-2 bg-white/10 rounded-full w-1/2 animate-pulse" />
                                            </div>
                                        ) : (
                                            <p className="text-sm md:text-base text-gray-200 leading-relaxed">
                                                {contract.aiSummary || "Evaluating opportunity potential..."}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>



                {/* SCROLLABLE CONTENT - TRIAGE MODE */}
                <div className="flex-1 overflow-y-auto p-6 custom-scrollbar bg-[#0A0A0A]">
                    <div className="space-y-6">

                        {/* Summary Actions */}
                        <div className="flex gap-4 mb-6">
                            <button
                                onClick={() => {
                                    onClose();
                                    navigate(`/contract/${contract.noticeId}`, { state: { contract } });
                                }}
                                className="flex-1 bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2"
                            >
                                <Building2 className="w-4 h-4" />
                                Open War Room
                            </button>
                            <button
                                onClick={handleSaveToggle}
                                className={`flex-1 font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2 border ${isSaved
                                    ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400'
                                    : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'
                                    }`}
                            >
                                <Star className={`w-4 h-4 ${isSaved ? 'fill-current' : ''}`} />
                                {isSaved ? 'Tracked' : 'Track Lead'}
                            </button>
                        </div>

                        {/* Quick AI Summary */}
                        <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-3">Snapshot</h3>
                            <p className="text-gray-300 text-sm leading-relaxed">
                                {contract.aiSummary || contract.description?.substring(0, 300) + "..."}
                            </p>
                        </div>

                        {/* Key Info Grid */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-white/5 border border-white/10 p-4 rounded-xl">
                                <h4 className="text-xs font-bold text-gray-500 uppercase">Set Aside</h4>
                                <p className="text-white font-medium mt-1">{contract.typeOfSetAside || "Open"}</p>
                            </div>
                            <div className="bg-white/5 border border-white/10 p-4 rounded-xl">
                                <h4 className="text-xs font-bold text-gray-500 uppercase">NAICS</h4>
                                <p className="text-white font-medium mt-1">{contract.naicsCode || "N/A"}</p>
                            </div>
                        </div>

                        {/* Description Preview */}
                        <div>
                            <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-widest mb-4">Description Preview</h4>
                            <div className="prose prose-invert max-w-none text-sm text-gray-300 leading-relaxed whitespace-pre-wrap line-clamp-6">
                                {contract.description || "No description provided."}
                            </div>
                            <a href={link} target="_blank" rel="noopener noreferrer" className="block mt-2 text-blue-400 text-xs hover:underline">
                                Read full solicitation on SAM.gov
                            </a>
                        </div>

                    </div>
                </div>

            </div >
        </div >
    );
}

